import { type TFunction } from 'i18next';
import _pick from 'lodash/pick';
import * as yup from 'yup';

import { FullTalentQuestionDTO, FullTalentQuestionDTOType, TalentQuestionsResponseDTO } from '@/api/generated';

import { DEFAULT_LANGUAGE_CODE, MULTI_OPTION_TYPES, OTHER_OPTION_ID, VALIDATION_RULE_ENUM } from './constants';

const withConditionalRequiredSchema = <T extends yup.AnyObject>(schema: yup.ObjectSchema<T>, isRequired?: boolean) =>
  isRequired ? schema.required() : schema.nullable();

const dynamicSchema = <T, TSchema extends yup.ISchema<T>, TContext>(
  valueSchema: yup.Schema | yup.Lazy<yup.InferType<TSchema>, TContext>,
  isRequired?: boolean
) =>
  yup.lazy(value =>
    withConditionalRequiredSchema(
      yup.object().shape(Object.fromEntries(Object.keys(value || {}).map(key => [key, valueSchema]))),
      isRequired
    )
  );

const questionSchema = (t: TFunction) =>
  yup.object().shape({
    id: yup.string().required(),
    isTalentEditable: yup.boolean().nullable(),
    type: yup.string<FullTalentQuestionDTOType>().oneOf(Object.values(FullTalentQuestionDTOType)).required(),
    isRequired: yup.boolean().required(),
    isActive: yup.boolean().required(),
    isKnockout: yup.boolean().required(),
    optionValues: yup
      .array()
      .when('type', {
        is: (type: FullTalentQuestionDTOType) => MULTI_OPTION_TYPES.includes(type),
        then: validation => validation.of(yup.string().required(t('validation:mandatory'))),
        otherwise: validation => validation
      })
      .nullable(),
    validation: yup
      .object()
      .shape({
        rule: yup.mixed<VALIDATION_RULE_ENUM>().oneOf(Object.values(VALIDATION_RULE_ENUM))
      })
      .nullable(),
    pattern: yup.string(),
    min: yup.number(),
    max: yup.number(),
    maxFileSize: yup.number(),
    allowedFileExtensions: yup.array().of(yup.string().required())
  });

export const getTalentRegistrationFormValidationSchema = (t: TFunction) =>
  yup
    .object<TalentQuestionsResponseDTO>()
    .transform((value: TalentQuestionsResponseDTO) => {
      const { external, internal, translations } = value || {};
      const transformedExternal = external?.filter(({ isActive }) => isActive) || [];
      const transformedInternal = internal?.filter(({ isActive }) => isActive) || [];
      const allQuestions = [...(transformedExternal || []), ...(transformedInternal || [])];
      const questionMap = allQuestions.reduce((acc, question) => ({ ...acc, [question.id!]: question }), {}) as {
        [key: string]: FullTalentQuestionDTO;
      };
      const allowedIds = allQuestions.map(({ id }: { id?: string }) => id!);

      const transformQuestions = ([questionKey, questionValue]: [
        string,
        {
          label?: string;
          optionLabels?: {
            [optionValue: string]: string;
          };
        }
      ]) => {
        const questionData = questionMap?.[questionKey];

        const mandatoryOptions =
          questionData && MULTI_OPTION_TYPES?.includes(questionData.type!)
            ? questionData?.optionValues?.filter((optionId: string) => optionId !== OTHER_OPTION_ID)
            : [];

        const optionLabelsTransformed = Object.fromEntries(
          mandatoryOptions?.map(key => [key, questionValue?.optionLabels?.[key] || '']) || []
        );

        return [
          questionKey,
          {
            ...questionValue,
            label: questionValue?.label || '',
            optionLabels: optionLabelsTransformed
          }
        ];
      };

      const transformedTranslations = Object.fromEntries(
        Object.entries(translations || {}).map(([languageKey, languageValue]) => {
          const tranformedLanguageValue = Object.fromEntries(Object.entries(languageValue).map(transformQuestions));

          return [languageKey, _pick(tranformedLanguageValue, allowedIds)];
        })
      );

      return {
        ...value,
        internal: transformedInternal,
        external: transformedExternal,
        translations: transformedTranslations
      };
    })
    .shape({
      external: yup.array().of(questionSchema(t)).required(),
      internal: yup
        .array()
        .of(questionSchema(t).omit(['isTalentEditable']))
        .required(),
      translations: yup.object().shape({
        [DEFAULT_LANGUAGE_CODE]: dynamicSchema(
          yup.object().shape({
            label: yup.string().min(1).required(),
            optionLabels: dynamicSchema(yup.string().min(1).required())
          }),
          true
        )
      })
    });

export const getTalentRegistrationFormWarningSchema = () =>
  yup
    .object<TalentQuestionsResponseDTO>()
    .transform((value: TalentQuestionsResponseDTO) => {
      const { external, internal, translations } = value || {};
      const transformedExternal = external?.filter(({ isActive }) => isActive) || [];
      const transformedInternal = internal?.filter(({ isActive }) => isActive) || [];
      const allQuestions = [...(transformedExternal || []), ...(transformedInternal || [])];
      const questionMap = allQuestions.reduce((acc, question) => ({ ...acc, [question.id!]: question }), {}) as {
        [key: string]: FullTalentQuestionDTO;
      };
      const allowedIds = allQuestions.map(({ id }: { id?: string }) => id!);

      const transformQuestions = ([questionKey, questionValue]: [
        string,
        {
          label?: string;
          optionLabels?: {
            [optionValue: string]: string;
          };
        }
      ]) => {
        const questionData = questionMap?.[questionKey];

        const mandatoryOptions =
          questionData && MULTI_OPTION_TYPES?.includes(questionData.type!)
            ? questionData?.optionValues?.filter((optionId: string) => optionId !== OTHER_OPTION_ID)
            : [];

        const optionLabelsTransformed = Object.fromEntries(
          mandatoryOptions?.map(key => [key, questionValue?.optionLabels?.[key] || '']) || []
        );

        return [
          questionKey,
          {
            ...questionValue,
            label: questionValue?.label || '',
            optionLabels: optionLabelsTransformed
          }
        ];
      };

      const transformedTranslations = Object.fromEntries(
        Object.entries(translations || {})
          .filter(([languageKey]) => languageKey !== DEFAULT_LANGUAGE_CODE)
          .map(([languageKey, languageValue]) => {
            const tranformedLanguageValue = Object.fromEntries(Object.entries(languageValue).map(transformQuestions));

            return [languageKey, _pick(tranformedLanguageValue, allowedIds)];
          })
      );

      return {
        translations: transformedTranslations
      };
    })
    .shape({
      translations: dynamicSchema(
        dynamicSchema(
          yup.object().shape({
            label: yup.string().min(1).required(),
            optionLabels: dynamicSchema(yup.string().min(1).required(), false)
          }),
          true
        ),
        true
      )
    });
